import { useState, useEffect, useCallback } from 'react'
import apiService from '../../../services/api'
import type { BrandEquityReport } from '../types'

interface UseBrandEquityReturn {
  report: BrandEquityReport | null
  isLoading: boolean
  isCalculating: boolean
  isDownloading: boolean
  isRegeneratingNarrative: boolean
  error: string | null
  fetchReport: () => Promise<void>
  recalculate: () => Promise<void>
  downloadPdf: () => Promise<void>
  regenerateNarrative: () => Promise<void>
}

export function useBrandEquity(userId: string): UseBrandEquityReturn {
  const [report, setReport] = useState<BrandEquityReport | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [isCalculating, setIsCalculating] = useState(false)
  const [isDownloading, setIsDownloading] = useState(false)
  const [isRegeneratingNarrative, setIsRegeneratingNarrative] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const fetchReport = useCallback(async () => {
    if (!userId) return
    try {
      setIsLoading(true)
      setError(null)
      const response = await apiService.get<{ status: string; data: BrandEquityReport }>(
        `/brand-equity/report?user_id=${userId}`
      )
      setReport(response.data)
    } catch (err: any) {
      setError(err.message || 'Failed to load Brand Equity report')
    } finally {
      setIsLoading(false)
    }
  }, [userId])

  const recalculate = useCallback(async () => {
    if (!userId) return
    try {
      setIsCalculating(true)
      setError(null)
      await apiService.post(`/brand-equity/calculate?user_id=${userId}`)
      await fetchReport()
    } catch (err: any) {
      setError(err.message || 'Failed to calculate Brand Equity')
    } finally {
      setIsCalculating(false)
    }
  }, [userId, fetchReport])

  const downloadPdf = useCallback(async () => {
    if (!userId) return
    try {
      setIsDownloading(true)
      setError(null)
      const blob = await apiService.getBlob(`/brand-equity/pdf?user_id=${userId}`)
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `brand_equity_report_${userId}.pdf`
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)
    } catch (err: any) {
      setError(err.message || 'Failed to download PDF')
    } finally {
      setIsDownloading(false)
    }
  }, [userId])

  const regenerateNarrative = useCallback(async () => {
    if (!userId) return
    try {
      setIsRegeneratingNarrative(true)
      setError(null)
      await apiService.post(`/brand-equity/narrative?user_id=${userId}`)
      await fetchReport()
    } catch (err: any) {
      setError(err.message || 'Failed to regenerate narrative')
    } finally {
      setIsRegeneratingNarrative(false)
    }
  }, [userId, fetchReport])

  useEffect(() => {
    fetchReport()
  }, [fetchReport])

  return { report, isLoading, isCalculating, isDownloading, isRegeneratingNarrative, error, fetchReport, recalculate, downloadPdf, regenerateNarrative }
}
